import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Send, Trash2, Plus, ChevronDown, ChevronUp, ChevronRight, X, Loader2, AlertTriangle, GitBranchPlus, GitCompareArrows, History } from 'lucide-react';
import { api } from '@/lib/api';
import {
  ClavePlantilla, Clausula, DiffPlantilla, ESTADO_PLANTILLA, FORMATO_CLAUSULA, FormatoClausula, NOMBRE_MODELO,
  PlantillaVersion, Preajuste, Seccion, Tabla, VariableDef, Variante, idNuevaClausula,
} from '@/lib/contratos';
import { formatDateTime, classNames } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import DiffPlantillaVista from '@/components/contratos/DiffPlantillaVista';
import HistorialLista from '@/components/contratos/HistorialLista';

function tablaATexto(t: Tabla | undefined): string {
  if (!t) return '';
  return t.filas.map(f => f.join(' | ')).join('\n');
}

function textoATabla(cabecera: string, cuerpo: string): Tabla {
  const cab = cabecera.trim() ? cabecera.split('|').map(c => c.trim()) : null;
  const filas = cuerpo.split('\n').map(l => l.trim()).filter(Boolean).map(l => l.split('|').map(c => c.trim()));
  return { cabecera: cab, filas };
}

interface EditorClausulaProps {
  clausula: Clausula;
  editable: boolean;
  onChange: (c: Clausula) => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
  onInsertAfter: () => void;
}

function EditorClausula({ clausula: c, editable, onChange, onDelete, onMove, onInsertAfter }: EditorClausulaProps) {
  const [abierta, setAbierta] = useState(false);
  const [cabecera, setCabecera] = useState((c.tabla?.cabecera || []).join(' | '));
  const [cuerpoTabla, setCuerpoTabla] = useState(tablaATexto(c.tabla));

  const set = (cambios: Partial<Clausula>) => onChange({ ...c, ...cambios });
  const setVariante = (i: number, cambios: Partial<Variante>) => {
    const variantes = [...(c.variantes || [])];
    variantes[i] = { ...variantes[i], ...cambios };
    set({ variantes });
  };

  if (c.formato === 'especial') {
    const nombre = c.especial === 'identificacion-titular' ? 'Identificación del titular (se rellena con la ficha; dos bloques si hay dos titulares)'
      : c.especial === 'identificacion-viviendas' ? 'Identificación de las viviendas (un bloque por vivienda, con licencia, referencia catastral o NRA y garaje)'
      : 'Bloque de firmas, con lugar y fecha como campos rellenables';
    return (
      <div className="rounded-lg border border-dashed border-sereno-dark/20 bg-sereno-blue/10 px-3 py-2 text-sm text-sereno-dark/70 flex items-center gap-2">
        <span className="badge bg-sereno-blue text-sereno-dark">Bloque de datos</span>
        <span className="flex-1">{nombre}</span>
        {editable && (
          <span className="flex items-center gap-1">
            <button onClick={() => onMove(-1)} className="p-1 rounded hover:bg-white/60" title="Subir"><ChevronUp size={14} /></button>
            <button onClick={() => onMove(1)} className="p-1 rounded hover:bg-white/60" title="Bajar"><ChevronDown size={14} /></button>
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={classNames('rounded-lg border bg-white', abierta ? 'border-sereno-dark/30 shadow-sm' : 'border-gray-100')}>
      <div className="flex items-start gap-2 px-3 py-2 cursor-pointer" onClick={() => setAbierta(a => !a)}>
        <span className="mt-0.5 text-sereno-dark/40">{abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        <span className="badge bg-gray-100 text-gray-600 whitespace-nowrap mt-0.5">{FORMATO_CLAUSULA[c.formato]}{c.nivel === 1 ? ' · 2º nivel' : ''}</span>
        {c.variantes && c.variantes.length > 0 && <span className="badge bg-amber-100 text-amber-800 whitespace-nowrap mt-0.5">{c.variantes.length} variante{c.variantes.length > 1 ? 's' : ''}</span>}
        <p className={classNames('text-sm flex-1 min-w-0', !abierta && 'line-clamp-2')}>
          {c.titulo && <strong>{c.titulo}{c.texto ? ': ' : ''}</strong>}{c.texto || (c.tabla ? `Tabla · ${c.tabla.filas.length} filas` : '')}
          {!c.titulo && !c.texto && !c.tabla && <span className="text-sereno-dark/30">(vacía)</span>}
        </p>
        <span className="text-[10px] font-mono text-sereno-dark/30 whitespace-nowrap hidden lg:inline" title="Identificador estable de la cláusula">{c.id}</span>
      </div>

      {abierta && (
        <div className="border-t border-gray-100 px-3 py-3 space-y-3" onClick={e => e.stopPropagation()}>
          <div className="grid gap-3 sm:grid-cols-[1fr_140px_120px]">
            <div>
              <label className="label">Título (va en negrita)</label>
              <input className="input" value={c.titulo || ''} disabled={!editable} onChange={e => set({ titulo: e.target.value || undefined })} />
            </div>
            <div>
              <label className="label">Formato</label>
              <select className="input" value={c.formato} disabled={!editable} onChange={e => set({ formato: e.target.value as FormatoClausula })}>
                {(['parrafo', 'vineta', 'numerada', 'subtitulo', 'tabla'] as FormatoClausula[]).map(f => <option key={f} value={f}>{FORMATO_CLAUSULA[f]}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Nivel</label>
              <select className="input" value={c.nivel ?? 0} disabled={!editable || (c.formato !== 'vineta' && c.formato !== 'numerada')} onChange={e => set({ nivel: Number(e.target.value) as 0 | 1 })}>
                <option value={0}>Primero</option>
                <option value={1}>Segundo (sangrado)</option>
              </select>
            </div>
          </div>
          {c.formato === 'tabla' ? (
            <div className="grid gap-3">
              <div>
                <label className="label">Cabecera (columnas separadas por |)</label>
                <input className="input font-mono text-sm" value={cabecera} disabled={!editable} onChange={e => { setCabecera(e.target.value); set({ tabla: textoATabla(e.target.value, cuerpoTabla) }); }} />
              </div>
              <div>
                <label className="label">Filas (una por línea, celdas separadas por |)</label>
                <textarea className="input font-mono text-sm" rows={6} value={cuerpoTabla} disabled={!editable} onChange={e => { setCuerpoTabla(e.target.value); set({ tabla: textoATabla(cabecera, e.target.value) }); }} />
              </div>
            </div>
          ) : (
            <div>
              <label className="label">Texto <span className="text-sereno-dark/40 font-normal">· las variables van entre dobles llaves, p. ej. {'{{renta_propietario}}'}</span></label>
              <textarea className="input text-sm leading-relaxed" rows={Math.min(12, Math.max(3, Math.ceil((c.texto || '').length / 110)))} value={c.texto || ''} disabled={!editable} onChange={e => set({ texto: e.target.value })} />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Variantes de redacción</label>
              {editable && (
                <button onClick={() => set({ variantes: [...(c.variantes || []), { clave: `variante-${(c.variantes || []).length + 1}`, nombre: '', texto: c.texto || '' }] })} className="text-xs text-sereno-dark/60 hover:text-sereno-dark flex items-center gap-1">
                  <Plus size={12} /> Añadir variante
                </button>
              )}
            </div>
            {(c.variantes || []).length === 0 ? (
              <p className="text-xs text-sereno-dark/40">Sin variantes: al generar solo se puede usar el texto estándar o retocarlo a mano.</p>
            ) : (
              <div className="space-y-2">
                {(c.variantes || []).map((v, i) => (
                  <div key={i} className="rounded-lg bg-amber-50/60 border border-amber-100 p-2 grid gap-2">
                    <div className="grid gap-2 sm:grid-cols-[150px_1fr_1fr_auto]">
                      <input className="input text-xs font-mono" placeholder="clave" value={v.clave} disabled={!editable} onChange={e => setVariante(i, { clave: e.target.value })} />
                      <input className="input text-sm" placeholder="Nombre que verá quien genera" value={v.nombre} disabled={!editable} onChange={e => setVariante(i, { nombre: e.target.value })} />
                      <input className="input text-sm" placeholder="Título (vacío = el de la cláusula)" value={v.titulo || ''} disabled={!editable} onChange={e => setVariante(i, { titulo: e.target.value || undefined })} />
                      {editable && <button onClick={() => set({ variantes: (c.variantes || []).filter((_, j) => j !== i) })} className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/40 hover:text-red-600" title="Quitar variante"><X size={14} /></button>}
                    </div>
                    {v.cuando && <p className="text-xs text-sereno-dark/60">Se aplica sola cuando <code className="font-mono">{v.cuando.variable}</code> es «{v.cuando.valor}»; no se elige al generar.</p>}
                    <textarea className="input text-sm leading-relaxed" rows={3} value={v.texto} disabled={!editable} onChange={e => setVariante(i, { texto: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {editable && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
              <button onClick={() => onMove(-1)} className="btn-ghost text-xs py-1 px-2 flex items-center gap-1"><ChevronUp size={12} /> Subir</button>
              <button onClick={() => onMove(1)} className="btn-ghost text-xs py-1 px-2 flex items-center gap-1"><ChevronDown size={12} /> Bajar</button>
              <button onClick={onInsertAfter} className="btn-ghost text-xs py-1 px-2 flex items-center gap-1"><Plus size={12} /> Insertar cláusula debajo</button>
              <span className="flex-1" />
              <button onClick={() => { if (confirm('¿Quitar esta cláusula de la plantilla?')) onDelete(); }} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1"><Trash2 size={12} /> Quitar</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlantillaEditorPage() {
  const { key, version } = useParams<{ key: string; version: string }>();
  const navigate = useNavigate();
  const clave = key as ClavePlantilla;
  const [plantilla, setPlantilla] = useState<PlantillaVersion | null>(null);
  const [preajustes, setPreajustes] = useState<Preajuste[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sucio, setSucio] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const [versiones, setVersiones] = useState<number[]>([]);
  const [comparar, setComparar] = useState<string>('');
  const [diff, setDiff] = useState<DiffPlantilla | null>(null);
  const [cargandoDiff, setCargandoDiff] = useState(false);
  const [verHistorial, setVerHistorial] = useState(false);

  useEffect(() => {
    setPlantilla(null);
    setError(null);
    setDiff(null);
    setComparar('');
    api.getPlantillaContrato(clave, Number(version)).then(p => { setPlantilla(p); setSucio(false); }).catch(e => setError(e.message));
    api.getPreajustesContrato(clave).then(setPreajustes).catch(() => setPreajustes([]));
    api.getPlantillasContrato().then(ms => {
      const m = ms.find(x => x.templateKey === clave);
      setVersiones((m?.versiones || []).map(v => v.version).filter(v => v !== Number(version)));
    }).catch(() => setVersiones([]));
  }, [clave, version]);

  const editable = plantilla?.estado === 'borrador';

  const abrirComparacion = async (otra: string) => {
    setComparar(otra);
    if (!otra || !plantilla) return;
    setCargandoDiff(true);
    try {
      const de = Math.min(Number(otra), plantilla.version);
      const a = Math.max(Number(otra), plantilla.version);
      setDiff(await api.getDiffPlantilla(clave, de, a));
    } catch (e: any) {
      alert(e.message || 'No se pudo comparar');
      setComparar('');
    } finally {
      setCargandoDiff(false);
    }
  };

  const avisos = useMemo(() => {
    if (!plantilla) return [];
    const ids = new Set(plantilla.secciones.flatMap(s => s.clausulas.map(c => c.id)));
    const lista: string[] = [];
    for (const p of preajustes) {
      const rotas = [...Object.keys(p.sobrescrituras || {}), ...Object.keys(p.variantes || {})].filter(id => !ids.has(id));
      if (rotas.length) lista.push(`El preajuste «${p.nombre}» toca ${rotas.length} cláusula${rotas.length > 1 ? 's' : ''} que no está${rotas.length > 1 ? 'n' : ''} en esta versión.`);
    }
    return lista;
  }, [plantilla, preajustes]);

  const actualizar = (cambios: Partial<PlantillaVersion>) => {
    setPlantilla(p => (p ? { ...p, ...cambios } : p));
    setSucio(true);
  };

  const actualizarSeccion = (i: number, cambios: Partial<Seccion>) => {
    if (!plantilla) return;
    const secciones = plantilla.secciones.map((s, j) => (j === i ? { ...s, ...cambios } : s));
    actualizar({ secciones });
  };

  const guardar = async () => {
    if (!plantilla) return;
    setGuardando(true);
    try {
      const p = await api.guardarPlantilla(clave, plantilla.version, {
        titulo: plantilla.titulo, nombre: plantilla.nombre, variables: plantilla.variables, secciones: plantilla.secciones, notas: plantilla.notas,
      });
      setPlantilla(p);
      setSucio(false);
    } catch (e: any) {
      alert(e.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const publicar = async () => {
    if (!plantilla) return;
    if (sucio) await guardar();
    if (!confirm(`¿Publicar la versión ${plantilla.version}? Los contratos nuevos partirán de ella; los ya generados no cambian.`)) return;
    setPublicando(true);
    try {
      const p = await api.publicarPlantilla(clave, plantilla.version);
      setPlantilla(p);
      if (p.publicacion) {
        const n = p.publicacion.preajustesMarcados;
        alert(`Versión ${plantilla.version} publicada${p.publicacion.cambios ? ` (${p.publicacion.cambios})` : ''}. ${n ? `${n} preajuste${n === 1 ? '' : 's'} tocan cláusulas que han cambiado y quedan marcados para revisar en Plantillas.` : 'Ningún preajuste se ve afectado.'}`);
      }
      setVersiones(vs => vs);
    } catch (e: any) {
      alert(e.message || 'No se pudo publicar');
    } finally {
      setPublicando(false);
    }
  };

  const eliminar = async () => {
    if (!plantilla || !confirm('¿Eliminar este borrador? Se pierden los cambios no publicados.')) return;
    try {
      await api.eliminarBorradorPlantilla(clave, plantilla.version);
      navigate('/contracts/plantillas');
    } catch (e: any) {
      alert(e.message || 'No se pudo eliminar');
    }
  };

  const nuevaVersion = async () => {
    if (!plantilla) return;
    try {
      const v = await api.crearBorradorPlantilla(clave, plantilla.version);
      navigate(`/contracts/plantillas/${clave}/${v.version}`);
    } catch (e: any) {
      alert(e.message || 'No se pudo crear el borrador');
    }
  };

  if (error) return <div className="card border border-red-200 bg-red-50 text-red-700">{error}</div>;
  if (!plantilla) return <LoadingSpinner />;

  const estado = ESTADO_PLANTILLA[plantilla.estado];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Link to="/contracts/plantillas" className="p-2 rounded-lg hover:bg-sereno-blue/30 text-sereno-dark/50"><ArrowLeft size={18} /></Link>
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {NOMBRE_MODELO[clave]} <span className="font-mono text-sm text-sereno-dark/50">v{plantilla.version}</span>
              <span className={`badge ${estado.className}`}>{estado.label}</span>
            </h2>
            <p className="text-sm text-sereno-dark/50">
              {plantilla.publicadaAt ? `Publicada el ${formatDateTime(plantilla.publicadaAt)}` : `Creada el ${formatDateTime(plantilla.createdAt)}`}
              {plantilla.notas ? ` · ${plantilla.notas}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {versiones.length > 0 && (
            <label className="flex items-center gap-1.5 text-sm text-sereno-dark/70">
              <GitCompareArrows size={14} />
              <select className="input w-auto py-1.5 text-sm" value={comparar} onChange={e => abrirComparacion(e.target.value)}>
                <option value="">Comparar con…</option>
                {versiones.map(v => <option key={v} value={String(v)}>v{v}</option>)}
              </select>
            </label>
          )}
          <button onClick={() => setVerHistorial(h => !h)} className="btn-ghost text-sm flex items-center gap-1.5"><History size={14} /> Historial</button>
          {editable ? (
            <>
              <button onClick={eliminar} className="btn-ghost text-sm text-red-500 flex items-center gap-1.5"><Trash2 size={14} /> Eliminar borrador</button>
              <button onClick={guardar} disabled={!sucio || guardando} className="btn-secondary text-sm flex items-center gap-1.5">
                {guardando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
              </button>
              <button onClick={publicar} disabled={publicando} className="btn-primary text-sm flex items-center gap-1.5">
                {publicando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Publicar
              </button>
            </>
          ) : (
            <button onClick={nuevaVersion} className="btn-primary text-sm flex items-center gap-1.5"><GitBranchPlus size={14} /> Nueva versión a partir de esta</button>
          )}
        </div>
      </div>

      {avisos.length > 0 && (
        <div className="card border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-1">{avisos.map((a, i) => <p key={i}>{a}</p>)}</div>
        </div>
      )}

      {!editable && (
        <p className="text-sm text-sereno-dark/50">Esta versión no se puede editar. Para cambiar la plantilla, abre una versión nueva: se crea en borrador y solo pasa a usarse al publicarla.</p>
      )}

      {verHistorial && (
        <section className="card">
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Historial de la versión {plantilla.version}</h3>
          <HistorialLista entradas={plantilla.historial} maximo={12} />
        </section>
      )}

      <Modal isOpen={!!comparar} onClose={() => { setComparar(''); setDiff(null); }} title={`Cambios entre la v${Math.min(Number(comparar) || plantilla.version, plantilla.version)} y la v${Math.max(Number(comparar) || plantilla.version, plantilla.version)}`} size="2xl">
        {cargandoDiff || !diff ? <LoadingSpinner /> : <DiffPlantillaVista diff={diff} />}
      </Modal>

      <section className="card">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Documento</h3>
        <div className="grid gap-3 md:grid-cols-[1fr_260px]">
          <div>
            <label className="label">Título del contrato (portada)</label>
            <input className="input" value={plantilla.titulo} disabled={!editable} onChange={e => actualizar({ titulo: e.target.value })} />
          </div>
          <div>
            <label className="label">Nombre corto</label>
            <input className="input" value={plantilla.nombre} disabled={!editable} onChange={e => actualizar({ nombre: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="label">Notas de la versión (qué cambia)</label>
            <input className="input" value={plantilla.notas || ''} disabled={!editable} onChange={e => actualizar({ notas: e.target.value })} />
          </div>
        </div>
      </section>

      <section className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Variables</h3>
          {editable && (
            <button onClick={() => actualizar({ variables: [...plantilla.variables, { clave: `variable_${plantilla.variables.length + 1}`, etiqueta: '', tipo: 'texto' }] })} className="btn-ghost text-xs py-1 flex items-center gap-1"><Plus size={12} /> Añadir variable</button>
          )}
        </div>
        <p className="text-xs text-sereno-dark/50 mb-3">Se escriben en el texto entre dobles llaves. Las calculadas salen de una expresión sobre otras variables, p. ej. <code className="font-mono">100 - renta_propietario</code>.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-sereno-dark/40">
                <th className="py-1 pr-2 font-semibold">Clave</th><th className="py-1 pr-2 font-semibold">Etiqueta</th><th className="py-1 pr-2 font-semibold">Tipo</th>
                <th className="py-1 pr-2 font-semibold">Por defecto</th><th className="py-1 pr-2 font-semibold">Calculada</th><th className="py-1 pr-2 font-semibold">Ayuda</th><th />
              </tr>
            </thead>
            <tbody>
              {plantilla.variables.map((v, i) => {
                const setV = (cambios: Partial<VariableDef>) => actualizar({ variables: plantilla.variables.map((x, j) => (j === i ? { ...x, ...cambios } : x)) });
                return (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="py-1 pr-2"><input className="input text-xs font-mono py-1" value={v.clave} disabled={!editable} onChange={e => setV({ clave: e.target.value })} /></td>
                    <td className="py-1 pr-2"><input className="input text-sm py-1" value={v.etiqueta} disabled={!editable} onChange={e => setV({ etiqueta: e.target.value })} /></td>
                    <td className="py-1 pr-2">
                      <select className="input text-sm py-1" value={v.tipo} disabled={!editable} onChange={e => setV({ tipo: e.target.value as VariableDef['tipo'] })}>
                        <option value="porcentaje">Porcentaje</option><option value="numero">Número</option><option value="texto">Texto</option>
                        {v.tipo === 'opcion' && <option value="opcion">Opción</option>}
                      </select>
                      {v.tipo === 'opcion' && (
                        <p className="text-[11px] text-sereno-dark/50 mt-0.5" title={v.desdeFicha ? 'Si nadie la elige, sale del modelo de la ficha' : undefined}>
                          {(v.opciones || []).map(o => `${o.valor} = ${o.etiqueta}`).join(' · ')}{v.desdeFicha ? ' · según la ficha' : ''}
                        </p>
                      )}
                      {v.desdeFecha === 'temporada_inicial' && (
                        <p className="text-[11px] text-sereno-dark/50 mt-0.5" title="Si nadie la fija, sale de la fecha de generación">
                          Primera temporada aún no empezada · según la fecha
                        </p>
                      )}
                    </td>
                    <td className="py-1 pr-2"><input className="input text-sm py-1" value={v.porDefecto ?? ''} disabled={!editable || !!v.calculada} onChange={e => setV({ porDefecto: e.target.value })} /></td>
                    <td className="py-1 pr-2"><input className="input text-xs font-mono py-1" value={v.calculada || ''} disabled={!editable} onChange={e => setV({ calculada: e.target.value || undefined })} /></td>
                    <td className="py-1 pr-2"><input className="input text-sm py-1" value={v.ayuda || ''} disabled={!editable} onChange={e => setV({ ayuda: e.target.value || undefined })} /></td>
                    <td className="py-1">{editable && <button onClick={() => actualizar({ variables: plantilla.variables.filter((_, j) => j !== i) })} className="p-1 rounded hover:bg-red-50 text-sereno-dark/40 hover:text-red-600"><X size={14} /></button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Secciones y cláusulas</h3>
          <div className="flex items-center gap-2 text-xs">
            <button onClick={() => setAbiertas(new Set(plantilla.secciones.map(s => s.id)))} className="btn-ghost py-1 px-2">Abrir todas</button>
            <button onClick={() => setAbiertas(new Set())} className="btn-ghost py-1 px-2">Cerrar todas</button>
          </div>
        </div>

        {plantilla.secciones.map((s, i) => {
          const abierta = abiertas.has(s.id);
          const cambiarClausulas = (clausulas: Clausula[]) => actualizarSeccion(i, { clausulas });
          return (
            <div key={s.id} className="card p-0 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-sereno-blue/10" onClick={() => setAbiertas(prev => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })}>
                {abierta ? <ChevronDown size={16} className="text-sereno-dark/40" /> : <ChevronRight size={16} className="text-sereno-dark/40" />}
                <span className={classNames('badge whitespace-nowrap', s.nivel === 1 ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-gray-600')}>{s.nivel === 1 ? 'Anexo' : 'Apartado'}</span>
                <span className="font-medium flex-1">{s.titulo || <span className="text-sereno-dark/40">Preámbulo (sin título)</span>}</span>
                <span className="text-xs text-sereno-dark/40">{s.clausulas.length} cláusulas</span>
              </div>
              {abierta && (
                <div className="border-t border-gray-100 px-4 py-4 space-y-2">
                  <div className="grid gap-3 sm:grid-cols-[1fr_160px] mb-2">
                    <div>
                      <label className="label">Título de la sección</label>
                      <input className="input" value={s.titulo} disabled={!editable} onChange={e => actualizarSeccion(i, { titulo: e.target.value })} />
                    </div>
                    <div>
                      <label className="label">Tipo</label>
                      <select className="input" value={s.nivel} disabled={!editable} onChange={e => actualizarSeccion(i, { nivel: Number(e.target.value) as 1 | 2 })}>
                        <option value={2}>Apartado del contrato</option>
                        <option value={1}>Anexo (empieza en página nueva)</option>
                      </select>
                    </div>
                  </div>
                  {s.clausulas.map((c, j) => (
                    <EditorClausula
                      key={c.id}
                      clausula={c}
                      editable={!!editable}
                      onChange={nc => cambiarClausulas(s.clausulas.map((x, k) => (k === j ? nc : x)))}
                      onDelete={() => cambiarClausulas(s.clausulas.filter((_, k) => k !== j))}
                      onMove={dir => {
                        const k = j + dir;
                        if (k < 0 || k >= s.clausulas.length) return;
                        const arr = [...s.clausulas];
                        [arr[j], arr[k]] = [arr[k], arr[j]];
                        cambiarClausulas(arr);
                      }}
                      onInsertAfter={() => {
                        const nueva: Clausula = { id: idNuevaClausula('nueva'), formato: 'parrafo', texto: '' };
                        const arr = [...s.clausulas];
                        arr.splice(j + 1, 0, nueva);
                        cambiarClausulas(arr);
                      }}
                    />
                  ))}
                  {editable && (
                    <button onClick={() => cambiarClausulas([...s.clausulas, { id: idNuevaClausula('nueva'), formato: 'parrafo', texto: '' }])} className="btn-ghost text-xs py-1 flex items-center gap-1"><Plus size={12} /> Añadir cláusula al final</button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {editable && (
          <button
            onClick={() => actualizar({ secciones: [...plantilla.secciones, { id: idNuevaClausula('seccion'), titulo: 'Nueva sección', nivel: 2, clausulas: [] }] })}
            className="btn-secondary text-sm flex items-center gap-1.5"
          >
            <Plus size={14} /> Añadir sección
          </button>
        )}
      </section>
    </div>
  );
}
